// PageDescription: 비급여 가격 지도 페이지
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import NonBenefitMapTemplate from '@/components/templates/nonBenefit/map/NonBenefitMapTemplate';
import useNonBenefitMapNearByPharmacySearchTabs from '@/hooks/nonBenefit/useNonBenefitMapNearbyPharmacySearchTabs';
import useNonBenefitMapViewModel from '@/hooks/nonBenefit/useNonBenefitMapViewModel';
import useNonBenefitMedicinesForSeo from '@/hooks/nonBenefit/useNonBenefitMedicinesForSeo';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { NonBenefitMapQueries } from '@/types/nonBenefit';
import { addQueryString } from '@/utils/url';

const NonBenefitMapPages = ({ client }: CustomPageProps) => {
  const viewModel = useNonBenefitMapViewModel(client);
  const { treatmentMedicineId, diagnosisTypeId } = viewModel.queries;

  const selectedMedicine = viewModel.selectedMedicine;

  const title = `${selectedMedicine?.treatmentProductMedicineName ?? ''} 최저가 약국 찾기 ${
    selectedMedicine?.medicineName ?? ''
  } 2025`;
  const description = `내 근처 약국의 ${selectedMedicine?.treatmentProductMedicineName ?? ''} ${
    selectedMedicine?.medicineName ?? ''
  } 가격을 지도에서 비교할 수 있어요 - ${selectedMedicine.medicineProductName}`;

  const canonical = `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.NON_BENEFIT.MAP.path}${addQueryString({
    diagnosisTypeId,
    treatmentMedicineId:
      viewModel.selectedTreatmentMedicine?.treatmentMedicine.treatmentMedicineId || treatmentMedicineId,
  })}`;

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonical}
        openGraph={{
          title,
          description: description,
          url: canonical,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_MAIN_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
      <NonBenefitMapTemplate
        client={client}
        viewModel={viewModel}
        title={`${selectedMedicine?.treatmentProductMedicineName} 최저가 약국 찾기`}
      />
    </>
  );
};

export default NonBenefitMapPages;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const queryClient = new QueryClient();
  const queries = ctx.query as NonBenefitMapQueries;

  const nonBenefitMedicines = await useNonBenefitMedicinesForSeo.fetchQuery(queryClient);

  /* 쿼리가 없는 경우 리다이렉트 */
  if (!queries.diagnosisTypeId) {
    return {
      redirect: {
        permanent: false,
        destination:
          ROUTES.NON_BENEFIT.MAP.path +
          addQueryString({
            diagnosisTypeId: nonBenefitMedicines.medicines[0].treatment.id,
            treatmentMedicineId: nonBenefitMedicines.medicines[0].id,
          }),
      },
    };
  }

  await Promise.allSettled([useNonBenefitMapNearByPharmacySearchTabs.prefetchQuery(queryClient)]);

  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
};
